import type { ObservabilityContext, ObservabilitySeverity, ObservabilitySink, ObservabilityUser } from './types';

/**
 * Module-level registry of observability sinks. Keyed by name so the same sink
 * can be re-registered idempotently (e.g. across React re-renders / StrictMode)
 * and so vendors can be added incrementally during composition.
 */
const sinks = new Map<string, ObservabilitySink>();

/**
 * Register (or replace, by name) observability sinks. This is the single knob a
 * vendor migration touches: add a PostHog/Sentry sink here and every
 * {@link reportError}/{@link reportMessage}/{@link identifyObservabilityUser}
 * call double-writes to it automatically.
 */
export function registerObservabilitySinks(next: ObservabilitySink[]): void {
  for (const sink of next) {
    sinks.set(sink.name, sink);
  }
}

/** Remove all registered sinks. Primarily for tests. */
export function clearObservabilitySinks(): void {
  sinks.clear();
}

/**
 * Fan out to every sink with per-sink isolation: one vendor throwing must never
 * prevent the others from receiving the event or break the caller.
 */
function forEachSink(run: (sink: ObservabilitySink) => void): void {
  for (const sink of sinks.values()) {
    try {
      run(sink);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`[observability] sink "${sink.name}" threw`, error);
    }
  }
}

/** Identify the current user across all registered observability vendors. */
export function identifyObservabilityUser(user: ObservabilityUser): void {
  forEachSink((sink) => sink.identifyUser(user));
}

/** Report an error across all registered observability vendors. */
export function reportError(cause: unknown, context?: ObservabilityContext): void {
  forEachSink((sink) => sink.reportError(cause, context));
}

/** Report a diagnostic message across all registered observability vendors. */
export function reportMessage(message: string, level: ObservabilitySeverity, context?: ObservabilityContext): void {
  forEachSink((sink) => sink.reportMessage(message, level, context));
}
